<template>
  <div class="food-window-component" :class="{ 'with-stop': item.hasStop }">
    <v-row>
      <v-col sm="8">
        <span class="window-title font-weight-medium">
          {{ item.name }}
        </span>
      </v-col>
      <v-col sm="4">
        <v-btn
          class="button button-back font-weight-medium"
          width="100%"
          color="#F1F3F6"
          @click="close"
        >
          <font-awesome-icon icon="chevron-left" />
          Вернуться назад
        </v-btn>
      </v-col>
    </v-row>

    <div class="food-info-section d-flex justify-space-between">
      <div class="food-image d-flex align-center justify-center">
        <div class="badge-left d-flex flex-column align-center">
          <component :is="getLeftSticker" />
          <icon-doubling v-if="isDoubling" />
        </div>

        <div class="badge-right d-flex flex-column align-center">
          <component :is="getRightSticker" />
        </div>
        <img
          v-if="item.imageOriginal"
          :src="item.imageOriginal"
          :alt="item.name"
        />
        <font-awesome-icon
          v-if="!item.image"
          icon="utensils"
          class="no-image"
        />
        <div v-if="item.hasStop" class="food-card_stop">
          На стопе
        </div>
      </div>

      <div class="food-info">
        <span class="label">Состав:</span>
        <span class="composition">{{ item.composition }}</span>
        <div class="food-characteristics d-flex a-center">
          <div class="characteristic">
            <span class="label">Вес:</span>
            <span class="unit font-weight-medium">
              <font-awesome-icon icon="weight-hanging" />
              {{ getWeight }}
            </span>
          </div>
          <div class="characteristic">
            <span class="label">Калорийность</span>
            <span class="unit font-weight-medium">{{ getCalories }}</span>
          </div>
          <div class="characteristic">
            <span class="label">Белки</span>
            <span class="unit font-weight-medium">{{ getProteins }}</span>
          </div>
          <div class="characteristic">
            <span class="label">Жиры</span>
            <span class="unit font-weight-medium">{{ getFats }}</span>
          </div>
          <div class="characteristic">
            <span class="label">Углеводы</span>
            <span class="unit font-weight-medium">{{ getCarbohydrates }}</span>
          </div>
        </div>

        <div class="food-price">
          <v-row class="d-flex align-end">
            <v-col sm="6">
              <span class="price-label">Стоимость товара:</span>
              <span class="price font-weight-medium">{{
                item.price | amountStringWithCurrency
              }}</span>
            </v-col>
            <v-col sm="6">
              <div
                v-if="foxCoinsAdd"
                class="d-flex align-center bonus-label-container"
              >
                <icon-bonus />
                <div>
                  <span class="bonus-label">Будет начисленно бонусов:</span>
                  <span
                    class="bonus font-weight-medium"
                  >+{{ foxCoinsAdd }} ₽</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <div class="button-group">
          <v-row>
            <v-col
              sm="10"
              offset-sm="2"
              offset-xl="0"
              xl="6"
            >
              <v-btn
                v-if="item.modifications && item.modifications.length"
                :loading="isAddModLoading"
                class="button button-half font-weight-medium"
                depressed
                @click="addModification"
              >
                <span class="icon">+M</span>
                {{ getModificationName }}
                <span class="price">{{ getModificationPrice }}</span>
              </v-btn>
            </v-col>
            <v-col
              sm="10"
              offset-sm="2"
              offset-xl="0"
              xl="6"
              class="text-right"
            >
              <v-btn
                :loading="isAddItemLoading"
                class="button button-add font-weight-medium"
                dark
                depressed
                @click="addItem"
              >
                <span
                  class="button-add-title d-flex align-center justify-space-between"
                >
                  <font-awesome-icon icon="plus" />
                  <span>Добавить в заказ</span>
                  <span class="price">{{
                    item.price | amountStringWithCurrency
                  }}</span>
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <div class="description-section">
      <span class="label">Описание:</span>
      <span class="description">{{ item.description }}</span>
      <div v-if="item.notes" class="notes">
        {{ item.notes }}
      </div>
    </div>

    <div v-if="item.type === MenuItemType.Bundle" class="description-section">
      <span class="label">Состав:</span>
      <bundle-items-carousel :bundle-items="item.bundleItems" />
    </div>

    <div v-else class="similar-food-section">
      <span class="label">Похожие блюда</span>
      <v-row>
        <v-col
          v-for="(similarItem, i) in similarItems.slice(0, 4)"
          :key="i"
          sm="3"
        >
          <food-card :item="similarItem" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import FoodCard from './FoodCard.vue'
  import mixinGetFoodStickers from '@/mixins/get-food-stickers'
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import IconDoubling from '@/components/order-processing/order-components/icons/IconDoubling.vue'
  import IconBonus from '@/components/icons/IconBonus.vue'
  import orderEdit from '@/mixins/order-edit'
  import MenuItemType from '@/models/enums/MenuItemType'
  import BundleItemsCarousel from '@/components/order-processing/order-components/first-step/BundleItemsCarousel.vue'

  export default {
    name: 'FoodWindow',
    components: {
      BundleItemsCarousel,
      FoodCard,
      IconDoubling,
      IconBonus,
    },
    mixins: [mixinGetFoodStickers, mixinSnackbarMessage, orderEdit],
    props: {
      item: {
        type: Object,
        default: () => ({}),
      },
    },
    data () {
      return {
        similarItems: [],
        isAddItemLoading: false,
        isAddModLoading: false,
      }
    },
    watch: {
      showFastOrderFoodWindow (val) {
        if (!val) {
          this.close()
        }
      },
      showNewOrderFoodWindow (val) {
        if (!val) {
          this.close()
        }
      },
    },
    computed: {
      MenuItemType () {
        return MenuItemType
      },
      ...mapState({
        basket: (state) => state.baskets.basket,
        showFastOrderFoodWindow: (state) => state.fastOrder.showFoodWindow,
        showNewOrderFoodWindow: (state) => state.newOrder.showFoodWindow,
      }),
      ...mapGetters({
        isNewOrderPage: 'app/isNewOrderPage',
      }),
      menuItems () {
        if (this.isNewOrderPage) {
          return this.$store.state.newOrder.menuItemsList
        }
        return this.$store.state.fastOrder.menuItemsList
      },
      getModificationName () {
        return this.item.modifications[0].modification
          ? this.item.modifications[0].modification.name
          : '–'
      },
      getModificationPrice () {
        return this.item.modifications[0].modification &&
          this.item.modifications[0].actualPrice
          ? this.item.modifications[0].actualPrice + ' '
          : '–'
      },
      foxCoinsAdd () {
        return this.item.foxCoinsAdd
      },
      getCalories () {
        return this.item.technicalCard
          ? this.item.technicalCard.calories + ' кКал'
          : '–'
      },
      getCarbohydrates () {
        return this.item.technicalCard
          ? this.item.technicalCard.carbohydrates + ' г'
          : '–'
      },
      getWeight () {
        return this.item.technicalCard
          ? this.item.technicalCard.weight + ' г'
          : '–'
      },
      getFats () {
        return this.item.technicalCard ? this.item.technicalCard.fats + ' г' : '–'
      },
      getProteins () {
        return this.item.technicalCard
          ? this.item.technicalCard.proteins + ' г'
          : '–'
      },
      isDoubling () {
        return this.item && this.item.isDoubling
      },
    },
    mounted () {
      this.similarItems = this.menuItems
      this.$store.commit(this.orderEditPrefix + '/setFoodWindowState', true)
    },
    methods: {
      addModification () {
        this.isAddModLoading = true
        const payload = {
          modificationId: this.item.modifications[0].id,
          menuItemId: this.item.id,
        }
        this.$store
          .dispatch('baskets/addBasketItem', {
            data: payload,
            id: this.basket.id,
          })
          .then((res) => {
            this.isAddModLoading = false
            if (res.status === 200) {
              this.showSuccessMessage('Добавлено в корзину')
              this.$store.commit('baskets/setBasket', res.data.data)
            } else {
              this.showErrorMessage(res.response.data.message)
            }
          })
      },
      addItem () {
        this.isAddItemLoading = true
        const payload = {
          menuItemId: this.item.id,
        }
        this.$store
          .dispatch('baskets/addBasketItem', {
            data: payload,
            id: this.basket.id,
          })
          .then((res) => {
            this.isAddItemLoading = false
            if (res.status === 200) {
              this.showSuccessMessage('Добавлено в корзину')
              this.$store.commit('baskets/setBasket', res.data.data)
            } else {
              this.showErrorMessage(res.response.data.message)
            }
          })
      },
      close () {
        if (this.isNewOrderPage) {
          this.$store.commit('newOrder/setFoodWindowState', false)
        } else {
          this.$store.commit('fastOrder/setFoodWindowState', false)
        }
        this.$emit('close')
      },
    },
  }
</script>

<style lang="less" scoped>
.food-window-component {
  font-size: 12px;

  .media-xl({
    font-size: 14px;
  });

  .window-title {
    font-size: 34px;
    line-height: 1;
  }

  .button-back {
    height: 80px;
    font-size: 15px;
    box-shadow: 0 3px 6px #00000029 !important;

    svg {
      margin-right: 8px;
    }

    svg {
      .fill-svg(#0033ff);
    }
  }

  .food-info-section {
    margin-bottom: 36px;
    padding-top: 30px;

    .media-sm({
      flex-direction: column;
    });

    .food-image {
      position: relative;
      background-color: @menu-item-image-bg;
      border-radius: 30px;
      max-width: 450%;
      width: 45%;
      padding: 10px;
      margin-right: 25px;
      z-index: 0;
      svg.no-image {
        width: 70px;
        height: 70px;
        opacity: 0.4;
        .fill-svg(@color-primary);
      }

      .media-xl({
        max-width: 432px;
        width: 100%;
        padding: 80px 46px 69px 46px;
      });

      .media-sm({
        width: 100%;
        margin-right: 0;
        margin-bottom: 25px;
      });

      .badge-left {
        position: absolute;
        top: 21px;
        left: 24px;
        z-index: 1;
        svg {
          margin-bottom: 10px;
        }
      }

      .badge-right {
        position: absolute;
        top: 21px;
        right: 24px;
        z-index: 1;
      }

      img {
        //max-height: 242px;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .food-info {
      flex: 1;

      .label {
        display: block;
        margin-bottom: 9px;
        font-size: 1.42em;
        line-height: 1;
      }

      .composition {
        display: block;
        margin-bottom: 22px;
        font-size: 1.14em;
        line-height: 1.3;
      }

      .food-characteristics {
        display: flex;
        width: 100%;

        .characteristic {
          flex-grow: 1;
          padding: 5px;
          border-radius: 7px;
          background-color: @color-secondary;
          margin-right: 5px;
          text-align: center;
          margin-bottom: 21px;

          &:last-child {
            margin-right: 0;
          }

          .label {
            font-size: 12px;
            margin-bottom: 5px;
          }

          .unit {
            font-size: 13px;
            line-height: 1;
          }
        }

        .media-xl({
          display: block;

          .characteristic {
            padding: 11px 18px 16px 18px;
            border-radius: 15px;
            margin-right: 10px;

            .label {
              font-size: 16px;
              margin-bottom: 10px;
            }

            .unit {
              font-size: 16px;
              line-height: 1;
            }
          }
        });
      }

      .food-price {
        margin-bottom: 28px;

        .price-label {
          display: block;
          font-size: 1.7em;
          line-height: 1;
        }

        .price {
          font-size: 3.3em;
          line-height: 1.1;
        }

        .bonus-label-container {
          svg {
            margin-right: 10px;
          }
          line-height: 1.1;
          margin-bottom: 10px;
        }

        .bonus-label {
          display: block;
          font-size: 1.14em;
          line-height: 1.3;
        }

        .bonus {
          display: block;
          font-size: 1.5em;
          line-height: 1;
        }
      }

      .button-group {
        .button {
          height: 5.7em;
          font-size: 1em;
          line-height: 1.2;
          border-radius: 10px;

          &.button-half {
            background-color: @color-secondary;

            .icon {
              display: inline-block;
              padding: 6px 5px;
              margin-right: 12px;
              border-radius: 6px;
              background-color: @color-primary;
              color: #ffffff;
            }

            .price {
              display: inline-block;
              margin-left: 19px;
            }
          }

          &.button-add {
            background-color: @color-primary;

            .button-add-title {
              svg {
                width: 19px;
                height: 19px;
                margin-right: 10px;
              }

              .price {
                margin-left: 25px;
              }
            }
          }
        }
      }
    }
  }

  .description-section {
    margin-bottom: 25px;
    .label {
      display: block;
      font-size: 1.7em;
      line-height: 1;
      margin-bottom: 14px;
    }
    .description {
      display: block;
      font-size: 1.14em;
      line-height: 1.3;
    }
    .notes {
      color: #e31e24;
      font-weight: 500;
      margin-top: 15px;
    }
  }

  .similar-food-section {
    margin-bottom: 30px;
    .label {
      display: block;
      font-size: 1.7em;
      line-height: 1;
    }
  }

  &.with-stop {
    .food-image {
      position: relative;

      img {
        opacity: 0.5;
        filter: blur(1px);
      }
      .food-card_stop {
        display: flex;
        align-items: center;
        padding: 10px;
        border-radius: 10px;
        position: absolute;
        top: 50%;
        left: 50%;
        background: #ff005f;
        color: #fff;
        font-size: 14px;
        text-transform: uppercase;
        font-weight: bold;
        white-space: nowrap;
        transform: translate(-50%, -50%);
        svg {
          width: 30px;
          height: 30px;
          margin-right: 10px;
          color: #fff;
          path {
            fill: #ffffff !important;
          }
        }
      }
    }
  }
}
</style>
